import { useEffect } from 'react'
import { useForm, type RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type { CreateCompanyRequest } from '@/api/model'
import {
  usePostApiCompanies,
  getGetApiCompaniesQueryKey,
  getGetApiCompaniesPaginatedQueryKey,
} from '@/api/generated/administrator-user/administrator-user'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'

type FormValues = {
  name: string
  street: string
  number: string
  extraAddressLine: string
  postalCode: string
  town: string
  country: string
  phoneNumber: string
  taxId: string
  invoiceEmail: string
}

const defaultValues: FormValues = {
  name: '', street: '', number: '', extraAddressLine: '', postalCode: '', town: '', country: '',
  phoneNumber: '', taxId: '', invoiceEmail: '',
}

// Deliberately loose, the backend has the final say.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Same company fields as the registration, but no company management user is created.
export function CreateCompanyDialog({ isOpen, onOpenChange }: Props) {
  const { t } = useTranslation(['super-admin-companies', 'common'])
  const queryClient = useQueryClient()
  // onChange: isValid re-renders only when validity flips, not on every keystroke.
  const { register, handleSubmit, reset, formState: { isValid } } = useForm<FormValues>({ defaultValues, mode: 'onChange' })

  useEffect(() => {
    if (isOpen) reset(defaultValues)
  }, [isOpen])

  const { mutate: createCompany, isPending } = usePostApiCompanies({
    mutation: {
      onSuccess: () => {
        notify.success(t('dialog.toasts.created'))
        onOpenChange(false)
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesPaginatedQueryKey() })
        // The company switcher in the header reads the unpaginated list.
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesQueryKey() })
      },
      onError: (error) => notify.apiError(error, t('dialog.toasts.createError')),
    },
  })

  const onValid = (values: FormValues) => {
    const payload: CreateCompanyRequest = {
      name: values.name,
      address: {
        street: values.street,
        number: values.number,
        extraAddressLine: values.extraAddressLine || null,
        postalCode: values.postalCode,
        town: values.town,
        country: values.country,
      },
      phoneNumber: values.phoneNumber || null,
      taxId: values.taxId || null,
      invoiceEmails: [values.invoiceEmail],
    }
    createCompany({ data: payload })
  }


  function field(id: keyof FormValues, required: boolean, props?: React.ComponentProps<typeof Input>, rules?: RegisterOptions<FormValues>) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={id} className="flex items-center gap-1">
          {t(`dialog.fields.${id}`)}
          {required && <span className="text-destructive">*</span>}
        </Label>
        <Input id={id} {...props} {...register(id, { required, ...rules })} />
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
          <DialogDescription>{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <form id="create-company-form" onSubmit={handleSubmit(onValid)} className="flex flex-col gap-4 py-2">
          {field('name', true, { maxLength: 120 }, { maxLength: 120 })}
          <div className="grid grid-cols-[1fr_6rem] gap-3">
            {field('street', true)}
            {field('number', true)}
          </div>
          {field('extraAddressLine', false)}
          <div className="grid grid-cols-[8rem_1fr] gap-3">
            {field('postalCode', true)}
            {field('town', true)}
          </div>
          {field('country', true)}
          {field('invoiceEmail', true, { type: 'email' }, { pattern: EMAIL_PATTERN })}
          <div className="grid grid-cols-2 gap-3">
            {field('phoneNumber', false)}
            {field('taxId', false, { maxLength: 25 }, { maxLength: 25 })}
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" form="create-company-form" disabled={!isValid || isPending}>
            {t('dialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
